import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage, useIntl } from 'react-intl'
import { Button, Grid } from '@mui/material'
import SubmitButton from 'components/form/SubmitButton'
import DateTimePicker from 'components/form/DateTimePicker'
import {
  cancelCourseHoleStats,
  checkCourseHoleStatsStatus,
  getCourseHoleStatsResults,
  initCourseHoleStats,
} from 'store/course/thunks'
import { selectCourse } from 'store/course/slice'
import LoadingSpinnerGreen from 'components/ui/LoadingSpinnerGreen'
import ErrorMessage from 'components/ui/ErrorMessage'
import { differenceInDays, isAfter, parseISO } from 'date-fns'

interface Props {
  course: Course
}

export default function CourseStats({ course }: Props): JSX.Element {
  const [processStarted, setProcessStarted] = useState<boolean>(false)
  const [pollingStarted, setPollingStarted] = useState<boolean>(false)
  const [pollingDone, setPollingDone] = useState<boolean>(false)
  const [validationError, setValidationError] = useState<string>('')
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout>()
  const intl = useIntl()
  const dispatch = useDispatch()
  const courseStatus = useSelector(selectCourse)
  const form = useForm<CourseStatsPayload>()
  const { handleSubmit, register } = form
  const {
    initCourseHoleStatsResponse,
    initCourseHoleStatsStatus,
    getCourseHoleStatsStatus,
    getCourseHoleStatsResultsStatus,
  } = courseStatus

  useEffect(() => {
    if (!pollingStarted && initCourseHoleStatsResponse?.taskId) {
      setPollingStarted(true)
      startPolling()
    }
  }, [initCourseHoleStatsResponse])

  useEffect(() => {
    const { getCourseHoleStatsStatus, getCourseHoleStatsResultsStatus } = courseStatus
    if (['failed'].includes(initCourseHoleStatsStatus || getCourseHoleStatsStatus || getCourseHoleStatsResultsStatus)) {
      resetProcessState()
    }
  }, [initCourseHoleStatsStatus, getCourseHoleStatsStatus, getCourseHoleStatsResultsStatus])

  const startPolling = () => {
    const intervalId = setInterval(async () => {
      if (initCourseHoleStatsResponse) {
        const status = await getStatsStatus(initCourseHoleStatsResponse.taskId)
        if (status.payload.status === 'completed' && !pollingDone) {
          clearInterval(intervalId)
          setPollingDone(true)
          getStatsResult(initCourseHoleStatsResponse.taskId as string)
        } else if (status.meta.requestStatus === 'rejected') {
          clearInterval(intervalId)
          resetProcessState()
        }
      }
    }, 10000) // Polling every 10 seconds (10000 milliseconds)
    setIntervalId(intervalId)
    // Set a timeout to stop the polling after 5 minutes (300000 milliseconds)
    setTimeout(() => {
      clearInterval(intervalId)
      resetProcessState()
    }, 300000)
  }

  const resetProcessState = () => {
    setProcessStarted(false)
    setPollingStarted(false)
  }

  const initStats = (dateFrom: string, dateTo: string) => {
    dispatch(initCourseHoleStats({ startDate: dateFrom, endDate: dateTo, courseId: course.id }))
  }

  const getStatsStatus = (taskId: string): any => {
    return dispatch(checkCourseHoleStatsStatus({ taskId: taskId, courseId: course.id }))
  }

  const getStatsResult = (taskId: string) => {
    dispatch(getCourseHoleStatsResults({ taskId: taskId, courseId: course.id }))
  }

  const cancelStats = (taskId: string) => {
    dispatch(cancelCourseHoleStats({ courseId: course.id, taskId: taskId }))
  }

  const submitForm = (data: CourseStatsPayload) => {
    setValidationError('')
    if (!validateForm(data)) {
      return
    }

    if (data.dateFrom && data.dateTo) {
      setPollingStarted(false)
      setPollingDone(false)
      setProcessStarted(true)
      initStats(data.dateFrom as string, data.dateTo as string)
    }
  }

  const handleCancel = () => {
    if (initCourseHoleStatsResponse) {
      cancelStats(initCourseHoleStatsResponse.taskId as string)
      clearInterval(intervalId)
      resetProcessState()
    }
  }

  const validateForm = (data: CourseStatsPayload): boolean => {
    if (isAfter(parseISO('2023-01-01'), data.dateFrom)) {
      setValidationError(intl.formatMessage({ id: 'strings.dateFromAfterLimit' }))
      return false
    }
    if (data.dateFrom > data.dateTo) {
      setValidationError(intl.formatMessage({ id: 'strings.dateFromBeforeDateTo' }))
      return false
    }
    // 366 enables the user to select eg. 2024-01-01 to 2025-01-01
    if (differenceInDays(data.dateTo, data.dateFrom) > 366) {
      setValidationError(intl.formatMessage({ id: 'strings.dateIntervalExceedsOneYear' }))
      return false
    }
    return true
  }

  return (
    <PageContainer>
      {course.id > 0 && (
        <>
          <SectionContainer>
            {processStarted && (
              <SpinnerContainer>
                <LoadingSpinnerGreen size={72} />
              </SpinnerContainer>
            )}
            <form onSubmit={handleSubmit(submitForm)}>
              <BasicData container>
                <TopContainer>
                  <SectionTitle>{intl.formatMessage({ id: 'strings.courseHoleStats' })}</SectionTitle>
                  <SubmitButton
                    disabled={processStarted}
                    status={'idle'}
                    text={intl.formatMessage({ id: 'strings.exportHolesStats' })}
                  />
                  <CancelButton onClick={handleCancel} disabled={!processStarted}>
                    <FormattedMessage id={'strings.cancel'} />
                  </CancelButton>
                </TopContainer>
                <MessageContainer item xs={8}>
                  {showErrorMessage(courseStatus.initCourseHoleStatsError, courseStatus.initCourseHoleStatsStatus)}
                  {showErrorMessage(courseStatus.getCourseHoleStatsError, courseStatus.getCourseHoleStatsStatus)}
                  {showErrorMessage(
                    courseStatus.getCourseHoleStatsResultsError,
                    courseStatus.getCourseHoleStatsResultsStatus,
                  )}
                  {validationError && <ErrorMessage>{validationError}</ErrorMessage>}
                  {processStarted && (
                    <InfoMessage>{intl.formatMessage({ id: 'strings.processHasStarted' })}</InfoMessage>
                  )}
                </MessageContainer>
                <FormTable>
                  <tbody>
                    <tr>
                      <td>
                        <DateTimePicker
                          disabled={processStarted}
                          register={register}
                          name="dateFrom"
                          label={intl.formatMessage({ id: 'strings.dateFrom' })}
                          dateOnly
                          required
                        />
                      </td>
                      <td>
                        <DateTimePicker
                          disabled={processStarted}
                          register={register}
                          name="dateTo"
                          label={intl.formatMessage({ id: 'strings.dateTo' })}
                          dateOnly
                          required
                        />
                      </td>
                    </tr>
                  </tbody>
                </FormTable>
              </BasicData>
            </form>
          </SectionContainer>
        </>
      )}
    </PageContainer>
  )
}

const showErrorMessage = (error: string | null, status: string): JSX.Element | null => {
  if (error && status === 'failed') {
    return <ErrorMessage>{error}</ErrorMessage>
  }
  return null
}

const PageContainer = styled.div`
  margin: 0 10px;
  padding: 0 0 20px 0;
  min-height: 500px;
  min-width: 1200px;

  & a {
    color: ${(props) => props.theme.palette.primary.main};
    text-decoration: none;
  }
`

const SectionContainer = styled.div`
  background-color: ${(props) => props.theme.customPalette.light};
  margin: 0 0 10px;
  padding: 20px;
`

const BasicData = styled(Grid)`
  padding: 0 20px;
`

const TopContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`

const SectionTitle = styled.h2`
  width: 100%;
  font-style: italic;
  font-weight: 800;
  font-size: 20px;
  line-height: 27px;
  color: ${(props) => props.theme.customPalette.darkGray2};
  margin-bottom: 25px;
`

const FormTable = styled.table`
  width: 100%;
  & tr {
    height: 40px;
    & td {
      letter-spacing: 0.4px;
      vertical-align: middle;
      padding: 5px 0;

      &:first-of-type {
        padding-right: 20px;
        text-align: right;
        width: 100px;
      }

      &.disabled {
        opacity: 0.5;
      }
    }
  }
`
const SpinnerContainer = styled.div`
  position: absolute;
  left: 50%;
  width: 100%;
  margin: 50px auto;
  justify-content: center;
`
const MessageContainer = styled(Grid)`
  margin: 20px 0;
`
const InfoMessage = styled(ErrorMessage)`
  background-color: rgba(255, 125, 0, 0.67);
`
const CancelButton = styled(Button)`
  &.Mui-disabled {
    opacity: 0.5;
  }
  background-color: rgba(255, 0, 0, 0.67);
  color: ${(props) => props.theme.customPalette.light};
  margin-left: 10px;
  text-transform: none;
`
